/**
 * /test-voice — split playground for the Chanl chat + voice widget.
 *
 *   LEFT COLUMN: <WidgetConfigForm /> — reusable, copyable component.
 *   RIGHT COLUMN: fake browser chrome with the widget launched inside it,
 *                 auto-opened for demo use.
 *
 * URL params still work as seed values:
 *   /test-voice?agent=ag_xxx&key=pub_xxx&theme=dark&color=%23c0673b
 */

import { useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { WidgetConfigForm, type WidgetConfigFormState } from '../components/widget-config-form';

export function TestVoiceRoute() {
  const [searchParams] = useSearchParams();

  // URL params override localStorage seed inside the form component.
  const initialFromUrl: Partial<WidgetConfigFormState> = useMemo(() => {
    const out: Partial<WidgetConfigFormState> = {};
    if (searchParams.get('agent')) out.agentId = searchParams.get('agent') ?? '';
    if (searchParams.get('key')) out.apiKey = searchParams.get('key') ?? '';
    if (searchParams.get('theme')) out.theme = (searchParams.get('theme') as 'dark' | 'light') ?? 'dark';
    if (searchParams.get('color')) out.color = searchParams.get('color') ?? '#c0673b';
    if (searchParams.get('position'))
      out.position = (searchParams.get('position') as 'bottom-right' | 'bottom-left') ?? 'bottom-right';
    if (searchParams.get('greeting')) out.greeting = searchParams.get('greeting') ?? '';
    return out;
  }, [searchParams]);

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'minmax(380px, 440px) 1fr',
        minHeight: '100dvh',
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        color: '#1a1a1a',
        background: '#f6f7f9',
      }}
    >
      {/* ── LEFT: config form (scrollable) ── */}
      <aside
        style={{
          borderRight: '1px solid #e5e7eb',
          padding: '1.5rem 1.75rem 3rem',
          overflowY: 'auto',
          maxHeight: '100dvh',
          background: '#f6f7f9',
        }}
      >
        <WidgetConfigForm initialValues={initialFromUrl} />
      </aside>

      {/* ── RIGHT: fake browser window with the widget inside ── */}
      <main style={{ position: 'relative', overflow: 'hidden', padding: '1.5rem' }}>
        <FakeBrowser />
      </main>
    </div>
  );
}

/**
 * FakeBrowser — a Chrome-styled frame with a skeleton page body. The Chanl
 * embed loader normally positions its launcher + chat panel + toasts with
 * `position: fixed` relative to the viewport. For this playground we want
 * them constrained INSIDE the browser chrome, so:
 *
 *   1. Inject a CSS override flipping `position: fixed` → `absolute` on
 *      `.chanl-btn`, `.chanl-chat`, `.chanl-toasts`.
 *   2. Make the inner page body `position: relative` so absolute-positioned
 *      children are anchored to it.
 *   3. On mount (and via MutationObserver, so subsequent destroy+init cycles
 *      are handled) reparent the chanl elements from `document.body` into
 *      the page body div.
 */
function FakeBrowser() {
  const pageBodyRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const content = pageBodyRef.current;
    if (!content) return;

    // 1. Inject CSS override (idempotent — guard by ID)
    const styleId = 'chanl-playground-override';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = `
        .chanl-btn, .chanl-chat, .chanl-toasts {
          position: absolute !important;
        }
        /* Toasts default to the same bottom-corner as the chat panel, which
           means they're hidden BEHIND it when the playground auto-opens the
           chat. Pin them to the top-right corner of the fake browser body
           and bump z-index so they always render on top. */
        .chanl-toasts {
          top: 20px !important;
          right: 20px !important;
          bottom: auto !important;
          left: auto !important;
          z-index: 2147483647 !important;
        }
        /* Mobile breakpoint in the loader uses !important to force full-screen.
           Re-override so the playground stays contained. */
        @media (max-width: 480px) {
          .chanl-chat {
            bottom: 88px !important;
            left: auto !important;
            right: 20px !important;
            width: 400px !important;
            height: 620px !important;
            border-radius: 16px !important;
          }
        }
        /* Skeleton shimmer for the page body placeholders */
        @keyframes chanl-skeleton-shimmer {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
      `;
      document.head.appendChild(style);
    }

    // 2. Reparent any existing chanl elements into the page body
    const reparent = () => {
      ['.chanl-btn', '.chanl-chat', '.chanl-toasts'].forEach((sel) => {
        document.querySelectorAll(sel).forEach((el) => {
          if (el.parentElement !== content) content.appendChild(el);
        });
      });
    };
    reparent();

    // 3. Watch document.body for future insertions (destroy+init creates new ones)
    const observer = new MutationObserver(() => reparent());
    observer.observe(document.body, { childList: true, subtree: false });

    return () => {
      observer.disconnect();
    };
  }, []);

  return (
    <div
      style={{
        position: 'relative',
        width: '100%',
        height: 'calc(100dvh - 3rem)',
        background: '#ffffff',
        borderRadius: 12,
        boxShadow: '0 20px 60px -15px rgba(0,0,0,0.15), 0 4px 12px rgba(0,0,0,0.05)',
        border: '1px solid #e5e7eb',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* ── Browser chrome ── */}
      <div
        style={{
          height: 44,
          background: '#f1f3f4',
          borderBottom: '1px solid #e5e7eb',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: '0 14px',
          flexShrink: 0,
        }}
      >
        {/* Traffic-light buttons (macOS style) */}
        <div style={{ display: 'flex', gap: 8 }}>
          <span style={{ width: 12, height: 12, borderRadius: '50%', background: '#ff5f57' }} />
          <span style={{ width: 12, height: 12, borderRadius: '50%', background: '#febc2e' }} />
          <span style={{ width: 12, height: 12, borderRadius: '50%', background: '#28c840' }} />
        </div>

        {/* URL bar */}
        <div
          style={{
            flex: 1,
            background: '#ffffff',
            border: '1px solid #e5e7eb',
            borderRadius: 6,
            padding: '5px 12px',
            fontSize: '0.75rem',
            color: '#525252',
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <span style={{ color: '#16a34a' }}>●</span>
          https://your-customer-site.com
        </div>

        {/* Window menu dots */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <span style={{ width: 3, height: 3, borderRadius: '50%', background: '#9ca3af' }} />
          <span style={{ width: 3, height: 3, borderRadius: '50%', background: '#9ca3af' }} />
          <span style={{ width: 3, height: 3, borderRadius: '50%', background: '#9ca3af' }} />
        </div>
      </div>

      {/* ── Page body (position:relative anchor for the reparented widget) ── */}
      <div
        ref={pageBodyRef}
        style={{
          flex: 1,
          overflow: 'hidden',
          background: '#ffffff',
          padding: '2.5rem 3rem',
          position: 'relative',
        }}
      >
        {/* Top nav skeleton */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '3rem',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <SkeletonBlock width={32} height={32} radius={8} />
            <SkeletonBlock width={90} height={14} />
          </div>
          <div style={{ display: 'flex', gap: 20 }}>
            <SkeletonBlock width={52} height={12} />
            <SkeletonBlock width={48} height={12} />
            <SkeletonBlock width={44} height={12} />
            <SkeletonBlock width={72} height={28} radius={6} />
          </div>
        </div>

        {/* Hero skeleton */}
        <div style={{ maxWidth: 560, marginBottom: '3rem' }}>
          <SkeletonBlock width="70%" height={32} />
          <div style={{ height: 10 }} />
          <SkeletonBlock width="90%" height={32} />
          <div style={{ height: 24 }} />
          <SkeletonBlock width="100%" height={12} />
          <div style={{ height: 8 }} />
          <SkeletonBlock width="85%" height={12} />
          <div style={{ height: 8 }} />
          <SkeletonBlock width="60%" height={12} />
          <div style={{ height: 24 }} />
          <div style={{ display: 'flex', gap: 10 }}>
            <SkeletonBlock width={120} height={36} radius={8} />
            <SkeletonBlock width={100} height={36} radius={8} />
          </div>
        </div>

        {/* Card grid skeleton */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: 16,
            maxWidth: 720,
          }}
        >
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              style={{
                background: '#fafafa',
                border: '1px solid #f1f3f5',
                borderRadius: 10,
                padding: '1.1rem',
              }}
            >
              <SkeletonBlock width={36} height={36} radius={8} />
              <div style={{ height: 12 }} />
              <SkeletonBlock width="70%" height={12} />
              <div style={{ height: 8 }} />
              <SkeletonBlock width="100%" height={10} />
              <div style={{ height: 6 }} />
              <SkeletonBlock width="85%" height={10} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/**
 * SkeletonBlock — neutral gray placeholder. Subtle shimmer via CSS gradient
 * keeps the right column alive without pulling attention from the widget.
 */
function SkeletonBlock({
  width,
  height,
  radius = 6,
}: {
  width: number | string;
  height: number;
  radius?: number;
}) {
  return (
    <div
      style={{
        width,
        height,
        borderRadius: radius,
        background: 'linear-gradient(90deg, #f1f3f5 0%, #e9ecef 50%, #f1f3f5 100%)',
        backgroundSize: '200% 100%',
        animation: 'chanl-skeleton-shimmer 2.2s ease-in-out infinite',
      }}
    />
  );
}
